'use client'

import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Badge } from '../ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import {
  AlertTriangle,
  Bell,
  Mail,
  Shield,
  UserCircle,
  Loader2,
  Check,
  Calendar,
  Info,
  Smartphone,
  CreditCard,
  KeyRound,
  Upload,
  RefreshCw,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { 
  updateProfileSchema, 
  changePasswordSchema, 
  accountDeletionSchema,
  UpdateProfileFormData,
  ChangePasswordFormData,
  AccountDeletionFormData
} from '../../lib/accountSchemas'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../lib/api'
import { notify } from '@/lib/notify'

// Current Subscription Component
function CurrentSubscription() {
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
  } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: () =>
      api
        .get('/billing/current-subscription')
        .then((res) => res.data?.data),
  })

  if (isLoadingSubscription) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading subscription...</span>
        </CardContent>
      </Card>
    )
  }

  if (subscriptionError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load subscription information
        </AlertDescription>
      </Alert>
    )
  }

  // Format price with currency symbol
  const formatPrice = (
    amount: number | null | undefined,
    currency: string | null | undefined
  ) => {
    if (amount == null || currency == null) return 'Free'

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase() || 'USD',
      minimumFractionDigits: 2,
    })

    return formatter.format(amount / 100)
  }

  const getBillingInterval = (interval: string | null | undefined) => {
    if (!interval) return ''
    return interval.toLowerCase() === 'month' ? 'monthly' : 'yearly'
  }

  return (
    <Card className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            <CardTitle>Current Subscription</CardTitle>
          </div>
          <div
            className={`flex items-center px-3 py-1 rounded-full ${
              currentSubscription?.status === 'active'
                ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                : currentSubscription?.status === 'past_due'
                ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                : 'bg-gray-50 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400'
            }`}
          >
            <Check className="h-3 w-3 mr-1" />
            <span className="text-xs font-medium">
              {currentSubscription?.status
                ? currentSubscription.status
                    .split('_')
                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                : 'Active'}
            </span>
          </div>
        </div>
        <CardDescription>
          Manage your subscription and view usage limits
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan Information */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {currentSubscription?.plan?.name || 'Free Plan'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Current plan
                </p>
                {currentSubscription?.amount > 0 && (
                  <Badge variant="outline" className="text-xs font-medium">
                    {formatPrice(
                      currentSubscription?.amount,
                      currentSubscription?.currency
                    )}
                    {currentSubscription?.recurringInterval && (
                      <span className="ml-1">
                        /{' '}
                        {getBillingInterval(
                          currentSubscription?.recurringInterval
                        )}
                      </span>
                    )}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Billing Dates */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
              <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Start Date
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentSubscription?.subscriptionStartDate
                    ? new Date(
                        currentSubscription?.subscriptionStartDate
                      ).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
              <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Next Payment
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentSubscription?.currentPeriodEnd
                    ? new Date(
                        currentSubscription?.currentPeriodEnd
                      ).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
              Usage Limits
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Daily
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentSubscription?.plan?.dailyLimit === -1
                    ? 'Unlimited'
                    : currentSubscription?.plan?.dailyLimit || '0'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Monthly
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentSubscription?.plan?.monthlyLimit === -1
                    ? 'Unlimited'
                    : currentSubscription?.plan?.monthlyLimit?.toLocaleString() ||
                      '0'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Bulk</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentSubscription?.plan?.bulkSendLimit === -1
                    ? 'Unlimited'
                    : currentSubscription?.plan?.bulkSendLimit || '0'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          {!currentSubscription?.plan?.name ||
          currentSubscription?.plan?.name?.toLowerCase() === 'free' ? (
            <Button className="bg-blue-600 hover:bg-blue-700">
              Upgrade to Pro →
            </Button>
          ) : (
            <Button variant="outline">
              Manage Subscription →
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Profile Information Component
function ProfileInformation({ currentUser, refetchCurrentUser }: any) {
  const updateProfileForm = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
    },
  })

  useEffect(() => {
    if (currentUser) {
      updateProfileForm.reset({
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
      })
    }
  }, [currentUser, updateProfileForm])

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    if (type === 'error') {
      notify.error(message)
    } else {
      notify.success(message)
    }
  }

  const {
    mutate: updateProfile,
    isPending: isUpdatingProfile,
    error: updateProfileError,
    isSuccess: isUpdateProfileSuccess,
  } = useMutation({
    mutationFn: (data: UpdateProfileFormData) =>
      api.put('/auth/update-profile', data),
    onSuccess: () => {
      refetchCurrentUser()
      showToast('Profile updated successfully!')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.message || 'Failed to update profile',
        'error'
      )
    },
  })

  const handleVerifyEmail = () => {
    showToast('Email verification not implemented yet', 'error')
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserCircle className="h-5 w-5" />
          <CardTitle>Profile Information</CardTitle>
        </div>
        <CardDescription>Update your profile information</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={updateProfileForm.handleSubmit((data) =>
            updateProfile(data)
          )}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              {...updateProfileForm.register('name')}
              placeholder="Enter your full name"
            />
            {updateProfileForm.formState.errors.name && (
              <p className="text-sm text-red-500">
                {updateProfileForm.formState.errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              Email Address
              {currentUser?.emailVerifiedAt && (
                <Badge variant="secondary" className="ml-2">
                  <Shield className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              )}
            </Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                {...updateProfileForm.register('email')}
                placeholder="Enter your email"
                disabled
              />
              {!currentUser?.emailVerifiedAt ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyEmail}
                  disabled={isUpdatingProfile}
                >
                  {isUpdatingProfile ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Verify
                </Button>
              ) : (
                <Button variant="outline" disabled>
                  <Check className="h-4 w-4 mr-2" />
                  Verified
                </Button>
              )}
            </div>
            {updateProfileForm.formState.errors.email && (
              <p className="text-sm text-red-500">
                {updateProfileForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              {...updateProfileForm.register('phone')}
              placeholder="Enter your phone number"
            />
            {updateProfileForm.formState.errors.phone && (
              <p className="text-sm text-red-500">
                {updateProfileForm.formState.errors.phone.message}
              </p>
            )}
          </div>

          {isUpdateProfileSuccess && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertDescription>
                Profile updated successfully!
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={isUpdatingProfile}
          >
            {isUpdatingProfile ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Save Changes
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// Change Password Component
function ChangePassword() {
  const changePasswordForm = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  })

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    if (type === 'error') {
      notify.error(message)
    } else {
      notify.success(message)
    }
  }

  const {
    mutate: changePassword,
    isPending: isChangingPassword,
    error: changePasswordError,
    isSuccess: isChangePasswordSuccess,
  } = useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      api.post('/auth/change-password', data),
    onSuccess: () => {
      showToast('Password changed successfully!')
      changePasswordForm.reset()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to change password'
      changePasswordForm.setError('root.serverError', {
        message: errorMessage,
      })
      showToast(errorMessage, 'error')
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Password</CardTitle>
        <CardDescription>
          Update your password to keep your account secure
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={changePasswordForm.handleSubmit((data) =>
            changePassword(data)
          )}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="oldPassword">Current Password</Label>
            <Input
              id="oldPassword"
              type="password"
              {...changePasswordForm.register('oldPassword')}
              placeholder="Enter your current password"
            />
            {changePasswordForm.formState.errors.oldPassword && (
              <p className="text-sm text-red-500">
                {changePasswordForm.formState.errors.oldPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              {...changePasswordForm.register('newPassword')}
              placeholder="Enter your new password"
            />
            {changePasswordForm.formState.errors.newPassword && (
              <p className="text-sm text-red-500">
                {changePasswordForm.formState.errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...changePasswordForm.register('confirmPassword')}
              placeholder="Confirm your new password"
            />
            {changePasswordForm.formState.errors.confirmPassword && (
              <p className="text-sm text-red-500">
                {changePasswordForm.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          {changePasswordForm.formState.errors.root?.serverError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {changePasswordForm.formState.errors.root.serverError.message}
              </AlertDescription>
            </Alert>
          )}

          {isChangePasswordSuccess && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertDescription>
                Password changed successfully!
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={isChangingPassword}
          >
            {isChangingPassword ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Change Password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

// Account Deletion Component
function AccountDeletion({ currentUser }: any) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('')

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    if (type === 'error') {
      notify.error(message)
    } else {
      notify.success(message)
    }
  }

  const {
    mutate: requestAccountDeletion,
    isPending: isRequestingAccountDeletion,
    error: requestAccountDeletionError,
    isSuccess: isRequestAccountDeletionSuccess,
  } = useMutation({
    mutationFn: () =>
      api.post('/account/request-account-deletion', {
        reason: deleteReason,
      }),
    onSuccess: () => {
      showToast('Account deletion request submitted successfully')
      setIsDeleteDialogOpen(false)
      setDeleteReason('')
      setDeleteConfirmEmail('')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.message || 'Failed to submit account deletion request',
        'error'
      )
    },
  })

  const handleDeleteAccount = () => {
    if (deleteConfirmEmail !== currentUser?.email) {
      showToast('Please enter your correct email address', 'error')
      return
    }
    requestAccountDeletion()
  }

  return (
    <Card className="border-red-200 dark:border-red-800">
      <CardHeader>
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertTriangle className="h-5 w-5" />
          <CardTitle>Danger Zone</CardTitle>
        </div>
        <CardDescription>
          Permanently delete your account and all associated data
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Delete Account
          </Button>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Delete Account
              </DialogTitle>
              <DialogDescription className="pt-4 space-y-4">
                <p>
                  Are you sure you want to delete your account? This action:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Cannot be undone</li>
                  <li>Will permanently delete all your data</li>
                  <li>Will cancel all active subscriptions</li>
                  <li>Will remove access to all services</li>
                </ul>

                <div className="space-y-2">
                  <Label htmlFor="deleteReason">Reason for deletion</Label>
                  <Textarea
                    id="deleteReason"
                    placeholder="Enter your reason for deletion"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmEmail">
                    Please type your email address to confirm:
                  </Label>
                  <Input
                    id="confirmEmail"
                    placeholder="Enter your email address"
                    value={deleteConfirmEmail}
                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                  />
                </div>

                {requestAccountDeletionError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {(requestAccountDeletionError as any).response?.data
                        ?.message ||
                        requestAccountDeletionError.message ||
                        'Failed to submit account deletion request'}
                    </AlertDescription>
                  </Alert>
                )}

                {isRequestAccountDeletionSuccess && (
                  <Alert>
                    <Check className="h-4 w-4" />
                    <AlertDescription>
                      Account deletion request submitted successfully
                    </AlertDescription>
                  </Alert>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isRequestingAccountDeletion}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={isRequestingAccountDeletion || !deleteConfirmEmail || !deleteReason}
              >
                {isRequestingAccountDeletion ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mr-2" />
                )}
                Delete Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

// Firebase Key Settings (super admin only)
function FirebaseKeySettings() {
  const [keyJson, setKeyJson] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const { data: keyInfo, refetch: refetchKeyInfo } = useQuery({
    queryKey: ['firebase-key-info'],
    queryFn: () => api.get('/admin/system/firebase-key').then((r) => r.data?.data),
  })

  const updateMutation = useMutation({
    mutationFn: (parsed: object) => api.put('/admin/system/firebase-key', parsed),
    onSuccess: () => {
      setKeyJson('')
      setSuccess(true)
      refetchKeyInfo()
      setTimeout(() => setSuccess(false), 4000)
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setKeyJson(ev.target?.result as string)
      setParseError(null)
      setSuccess(false)
    }
    reader.readAsText(file)
  }

  const handleSubmit = () => {
    setParseError(null)
    setSuccess(false)
    let parsed: object
    try {
      parsed = JSON.parse(keyJson)
    } catch {
      setParseError('Invalid JSON — paste the full contents of the downloaded service account JSON file.')
      return
    }
    updateMutation.mutate(parsed)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-orange-500" />
          Firebase Service Account Key
        </CardTitle>
        <CardDescription>
          Manages FCM push delivery to registered Android gateway devices. Replace when the current key is revoked or expired.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="border-blue-200 bg-blue-50 text-blue-800">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>Not</strong> <code>google-services.json</code>. You need the <strong>Admin SDK service account key</strong>: Firebase Console → Project Settings → <strong>Service accounts</strong> tab → <em>Generate new private key</em>. The downloaded JSON contains <code>"type": "service_account"</code>.
          </AlertDescription>
        </Alert>
        {keyInfo && (
          <div className="rounded-md border p-4 bg-gray-50 dark:bg-gray-900 space-y-1 text-sm">
            <div className="flex items-center gap-2 text-gray-500">
              <span className="font-medium text-gray-700 dark:text-gray-300 w-28">Project:</span>
              <span className="font-mono">{keyInfo.projectId}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <span className="font-medium text-gray-700 dark:text-gray-300 w-28">Client email:</span>
              <span className="font-mono text-xs break-all">{keyInfo.clientEmail}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <span className="font-medium text-gray-700 dark:text-gray-300 w-28">Key ID:</span>
              <span className="font-mono text-xs">{keyInfo.privateKeyId}</span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>New service account JSON</Label>
          <div className="flex items-center gap-2 mb-2">
            <label className="cursor-pointer inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <Upload className="h-4 w-4" />
              Upload JSON file
              <input type="file" accept=".json,application/json" className="hidden" onChange={handleFileChange} />
            </label>
            <span className="text-gray-400 text-sm">or paste below</span>
          </div>
          <Textarea
            rows={8}
            placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
            value={keyJson}
            onChange={(e) => { setKeyJson(e.target.value); setParseError(null); setSuccess(false) }}
            className="font-mono text-xs"
          />
        </div>

        {parseError && (
          <Alert variant="destructive">
            <AlertDescription>{parseError}</AlertDescription>
          </Alert>
        )}

        {updateMutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {(updateMutation.error as any)?.response?.data?.message ?? 'Failed to update key. Check the JSON and try again.'}
            </AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <Check className="h-4 w-4" />
            <AlertDescription>Firebase key updated — SDK re-initialized successfully.</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-3 pt-1">
          <Button
            onClick={handleSubmit}
            disabled={!keyJson.trim() || updateMutation.isPending}
            className="flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Updating...</>
            ) : (
              <><RefreshCw className="h-4 w-4" /> Update Key</>
            )}
          </Button>
          <p className="text-xs text-gray-500">
            Generate a new key from Firebase Console → Project Settings → Service Accounts.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

// Notification Settings Component
function NotificationSettings() {
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  const { data: settings, isLoading: loadingSettings, refetch: refetchSettings } = useQuery({
    queryKey: ['notificationSettings'],
    queryFn: () => api.get('/profile/notification-settings').then(r => r.data?.data?.notificationSettings),
  })

  const { data: adminUsers, isLoading: loadingAdmins } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => api.get('/profile/admin-users').then(r => r.data?.data?.admins || []),
  })

  const selectedAdminId = settings?.adminUserId || ''

  const { data: adminDevices, isLoading: loadingDevices } = useQuery({
    queryKey: ['adminDevices', selectedAdminId],
    queryFn: () => api.get(`/profile/user-devices/${selectedAdminId}`).then(r => r.data?.data?.devices || []),
    enabled: !!selectedAdminId,
  })

  const [localEnabled, setLocalEnabled] = useState(false)
  const [localAdminId, setLocalAdminId] = useState('')
  const [localDeviceIds, setLocalDeviceIds] = useState<string[]>([])

  useEffect(() => {
    if (settings) {
      setLocalEnabled(settings.deviceOfflineEnabled || false)
      setLocalAdminId(settings.adminUserId || '')
      setLocalDeviceIds((settings.monitoredDeviceIds || []).map(String))
    }
  }, [settings])

  const handleSave = async () => {
    setSaving(true)
    setSuccessMsg('')
    try {
      await api.put('/profile/notification-settings', {
        deviceOfflineEnabled: localEnabled,
        adminUserId: localAdminId || null,
        monitoredDeviceIds: localDeviceIds,
      })
      setSuccessMsg('Notification settings saved.')
      refetchSettings()
    } catch {
      setSuccessMsg('Failed to save settings.')
    }
    setSaving(false)
  }

  const toggleDevice = (id: string) => {
    setLocalDeviceIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  if (loadingSettings) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading notification settings...</span>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Device Offline Notifications
          </CardTitle>
          <CardDescription>
            Get notified via email and in-app when monitored devices go offline.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="offline-toggle" className="text-sm font-medium">
              Enable device offline notifications
            </Label>
            <button
              id="offline-toggle"
              role="switch"
              aria-checked={localEnabled}
              onClick={() => setLocalEnabled(!localEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {localEnabled && (
            <>
              {/* Admin user dropdown */}
              <div className="space-y-2">
                <Label htmlFor="admin-select">Notify Org Admin</Label>
                {loadingAdmins ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading admins...
                  </div>
                ) : (
                  <select
                    id="admin-select"
                    value={localAdminId}
                    onChange={e => {
                      setLocalAdminId(e.target.value)
                      setLocalDeviceIds([])
                    }}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  >
                    <option value="">Select an admin user...</option>
                    {(adminUsers || []).map((a: any) => (
                      <option key={a._id} value={a._id}>
                        {a.name} ({a.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Devices list */}
              {localAdminId && (
                <div className="space-y-2">
                  <Label>Monitored Devices</Label>
                  {loadingDevices ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading devices...
                    </div>
                  ) : !adminDevices?.length ? (
                    <p className="text-sm text-gray-500">No devices registered for this admin.</p>
                  ) : (
                    <div className="space-y-2">
                      {adminDevices.map((d: any) => {
                        const label = d.displayName || d.deviceModel || `${d.gatewayType} device`
                        const checked = localDeviceIds.includes(d._id)
                        return (
                          <label key={d._id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDevice(d._id)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600"
                            />
                            <Smartphone className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{label}</span>
                            <Badge variant={d.enabled ? 'default' : 'secondary'} className="ml-auto text-xs">
                              {d.gatewayType}
                            </Badge>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Save */}
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>
              ) : (
                <><Check className="h-4 w-4 mr-1" /> Save Notifications</>
              )}
            </Button>
            {successMsg && (
              <span className="text-sm text-green-600 dark:text-green-400">{successMsg}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Main AccountSettings Component
export default function AccountSettings() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('subscription')

  const {
    data: currentUser,
    isLoading: isLoadingUser,
    refetch: refetchCurrentUser,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () =>
      api
        .get('/auth/whoami')
        .then((res) => res.data?.data),
  })

  const isSuperAdmin = user?.role?.toUpperCase() === 'ADMIN'

  const tabs = [
    {
      id: 'subscription',
      name: 'Subscription',
      icon: CreditCard,
    },
    {
      id: 'profile',
      name: 'Profile',
      icon: UserCircle,
    },
    {
      id: 'security',
      name: 'Security',
      icon: Shield,
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: Bell,
    },
    {
      id: 'danger',
      name: 'Danger Zone',
      icon: AlertTriangle,
    },
    ...(isSuperAdmin ? [{
      id: 'system',
      name: 'System',
      icon: KeyRound,
    }] : []),
  ]

  if (isLoadingUser) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading account settings...</p>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'subscription':
        return <CurrentSubscription />
      case 'profile':
        return (
          <ProfileInformation 
            currentUser={currentUser} 
            refetchCurrentUser={refetchCurrentUser}
          />
        )
      case 'security':
        return <ChangePassword />
      case 'notifications':
        return <NotificationSettings />
      case 'danger':
        return <AccountDeletion currentUser={currentUser} />
      case 'system':
        return isSuperAdmin ? <FirebaseKeySettings /> : null
      default:
        return <CurrentSubscription />
    }
  }

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8 px-6 lg:px-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  isActive
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon 
                  className={`-ml-0.5 mr-2 h-5 w-5 ${
                    isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6 px-6 lg:px-8">
        {renderTabContent()}
      </div>
    </div>
  )
}